import { createFileRoute } from "@tanstack/react-router";
import { Calendar, Linkedin, Mail } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { submitContact } from "@/lib/contact.functions";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

      { title: "Contact Scott Cunningham" },
      {
        name: "description",
        content:
          "Contact Scott Cunningham (@scottcbusiness) about content strategy, copywriting, social media, and AI workflow services for tech brands.",
      },
      { property: "og:title", content: "Contact Scott Cunningham" },
      {
        property: "og:description",
        content:
          "Contact Scott Cunningham (@scottcbusiness) about content strategy, copywriting, social media, and AI workflow services for tech brands.",
      },
      { property: "og:url", content: "https://scottcbusiness.com/contact" },
      { name: "twitter:title", content: "Contact Scott Cunningham" },
      {
        name: "twitter:description",
        content:
          "Contact Scott Cunningham (@scottcbusiness) about content strategy, copywriting, social media, and AI workflow services for tech brands.",
      },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://scottcbusiness.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "Contact",
              item: "https://scottcbusiness.com/contact",
            },
          ],
        }),
      },
    ],
  }),
  component: ContactPage,
});

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name too long"),
  email: z.string().trim().email("Invalid email").max(255),
  message: z.string().trim().min(1, "Message is required").max(2000, "Message too long"),
});

function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof typeof form, string>>>({});
  const submit = useServerFn(submitContact);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const parsed = contactSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: typeof errors = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof typeof form;
        fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await submit({ data: { ...parsed.data, website: form.website } });
      setSent(true);
      setForm({ name: "", email: "", message: "", website: "" });
      toast.success("Message sent. I'll reply within 48 hours.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Couldn't send your message.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="relative isolate overflow-hidden grain">
      <LightSectionBackdrop variant="abstract" />
      <div className="blob float-slow -left-32 -top-24 h-[420px] w-[420px] bg-[var(--color-primary)]" />
      <div
        className="blob float-slow right-[-120px] top-40 h-[360px] w-[360px] bg-[var(--color-electric)]"
        style={{ animationDelay: "-3s" }}
      />
      <div
        className="blob float-slow bottom-[-160px] left-1/3 h-[480px] w-[480px] bg-[var(--color-violet)]"
        style={{ animationDelay: "-6s" }}
      />
      <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-32">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
          Get in touch
        </p>
        <h1 className="mt-6 max-w-5xl font-display text-[clamp(2.5rem,8vw,7rem)] leading-[0.95] text-balance">
          Tell me what you’re building <em className="italic text-gradient">or who you’re looking for.</em>
        </h1>
        <p className="mt-8 max-w-2xl text-lg text-foreground/90 md:text-muted-foreground">
          I'm currently open to fractional, full-time, and project engagements in social media
          leadership and content strategy. Quickest way to reach me is email or LinkedIn, I reply
          within 48 hours.
        </p>

        <a
          href="https://calendly.com/scottthomascunningham/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="group mt-12 inline-flex items-center justify-center gap-3 rounded-full bg-gradient-hero px-16 py-6 font-mono text-sm uppercase tracking-[0.2em] text-white lift-glow shadow-[0_8px_28px_-6px_oklch(0.62_0.24_28/0.55)]"
        >
          <Calendar className="h-6 w-6 shrink-0 transition-transform group-hover:-translate-y-0.5" />
          Book a 30-min call
          <span aria-hidden>→</span>
        </a>

        <div className="mt-12 grid stat-grid gap-px rounded-md border-2 border-foreground bg-foreground shadow-pop md:grid-cols-2">
          <a
            href="mailto:scottcbusiness@protonmail.com"
            className="group flex items-center justify-between gap-4 bg-background p-10 transition-colors hover:bg-gradient-acid"
          >
            <div className="min-w-0 flex-1">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Email
              </p>
              <p className="mt-3 font-display text-lg sm:text-xl md:text-2xl">
                scottcbusiness<wbr />@protonmail.com
              </p>
            </div>
            <Mail
              className="h-6 w-6 shrink-0 transition-transform group-hover:-translate-y-1"
              style={{ color: "var(--color-primary)" }}
            />
          </a>
          <a
            href="https://www.linkedin.com/in/scottcbusiness/"
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center justify-between bg-background p-10 transition-colors hover:bg-gradient-acid"
          >
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                LinkedIn
              </p>
              <p className="mt-3 font-display text-2xl md:text-3xl">/in/scottcbusiness</p>
            </div>
            <Linkedin
              className="h-6 w-6 transition-transform group-hover:-translate-y-1"
              style={{ color: "var(--color-electric)" }}
            />
          </a>
        </div>

        <div className="mt-20 grid gap-10 md:grid-cols-12">
          <div className="md:col-span-4">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Send a message
            </p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight">
              Prefer a <em className="italic text-gradient">form?</em>
            </h2>
            <p className="mt-4 text-muted-foreground">
              Drop your details below and I'll get back to you directly. Replies usually within 48
              hours.
            </p>
          </div>
          <form
            onSubmit={handleSubmit}
            noValidate
            className="md:col-span-8 rounded-md border-2 border-foreground bg-background p-8 shadow-pop space-y-6"
          >
            {/* Honeypot — hidden from real users, bots fill it in */}
            <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
              <label htmlFor="website">Website</label>
              <input
                id="website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="name"
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
                >
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  required
                  maxLength={100}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full border-b-2 border-foreground bg-transparent py-2 font-display text-lg outline-none focus:border-[var(--color-primary)]"
                />
                {errors.name && <p className="mt-1 text-xs text-destructive">{errors.name}</p>}
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
                >
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  maxLength={255}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-2 w-full border-b-2 border-foreground bg-transparent py-2 font-display text-lg outline-none focus:border-[var(--color-primary)]"
                />
                {errors.email && <p className="mt-1 text-xs text-destructive">{errors.email}</p>}
              </div>
            </div>
            <div>
              <label
                htmlFor="message"
                className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground"
              >
                Message
              </label>
              <textarea
                id="message"
                required
                rows={6}
                maxLength={2000}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-2 w-full resize-y border-b-2 border-foreground bg-transparent py-2 font-display text-lg outline-none focus:border-[var(--color-primary)]"
              />
              {errors.message && <p className="mt-1 text-xs text-destructive">{errors.message}</p>}
              <p className="mt-1 text-right font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {form.message.length}/2000
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {sent ? "Message received ✓" : "I'll reply within 48 hours"}
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-background transition-transform hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
              >
                {submitting ? "Sending..." : "Send Message →"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
